import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { User, Mail, Phone, Calendar, CreditCard } from "lucide-react";

const subscriptionTypeLabels = {
  monthly: "Monatliches Abo",
  bq_intensiv: "BQ Intensiv",
  hq_intensiv: "HQ Intensiv"
};

const Profile = () => {
  const { user } = useAuth();
  const subscription = user?.subscription;

  const getStatusClass = (status) => {
    switch (status) {
      case "active":
        return "status-active";
      case "expiring_soon":
        return "status-warning";
      case "expired":
        return "status-expired";
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Aktiv";
      case "expiring_soon":
        return "Läuft bald ab";
      case "expired":
        return "Abgelaufen";
      default:
        return "Unbekannt";
    }
  };

  return (
    <ParticipantLayout title="Mein Profil">
      <div className="space-y-6" data-testid="participant-profile">
        {/* Profile Header */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-sm bg-primary/10 flex items-center justify-center">
                <User className="h-10 w-10 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
                {subscription && (
                  <Badge className={`${getStatusClass(subscription.status)} rounded-sm mt-2`}>
                    {getStatusText(subscription.status)}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Information */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Kontaktdaten
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">Name</p>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">E-Mail</p>
                    <p className="font-mono text-sm">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">Telefon</p>
                    <p className="font-mono">{user?.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">Registriert am</p>
                    <p className="font-mono text-sm">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString("de-DE") : "-"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Details */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Abonnement Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {subscription ? (
                <div className="divide-y divide-border">
                  <div className="flex items-center justify-between p-4">
                    <span className="text-muted-foreground">Abo-Typ</span>
                    <span className="font-medium">
                      {subscriptionTypeLabels[subscription.subscription_type]}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className={`${getStatusClass(subscription.status)} rounded-sm`}>
                      {getStatusText(subscription.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-4">
                    <span className="text-muted-foreground">Startdatum</span>
                    <span className="font-mono">{subscription.start_date}</span>
                  </div>
                  <div className="flex items-center justify-between p-4">
                    <span className="text-muted-foreground">Enddatum</span>
                    <span className="font-mono">
                      {subscription.is_unlimited ? (
                        <span className="text-green-500">Unbegrenzt</span>
                      ) : (
                        subscription.end_date
                      )}
                    </span>
                  </div>
                  {subscription.days_remaining !== null && (
                    <div className="flex items-center justify-between p-4">
                      <span className="text-muted-foreground">Restlaufzeit</span>
                      <span className={`font-bold ${
                        subscription.status === "active" ? "text-green-500" :
                        subscription.status === "expiring_soon" ? "text-yellow-500" :
                        "text-red-500"
                      }`}>
                        {subscription.days_remaining} Tage
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Kein aktives Abonnement</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Account Status */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg">Kontostatus</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Account Status</p>
                <p className="text-sm text-muted-foreground">
                  {user?.is_locked 
                    ? "Ihr Konto ist derzeit gesperrt. Kontaktieren Sie den Administrator."
                    : "Ihr Konto ist aktiv und funktionsfähig."}
                </p>
              </div>
              <Badge className={user?.is_locked ? "status-expired" : "status-active"}>
                {user?.is_locked ? "Gesperrt" : "Aktiv"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </ParticipantLayout>
  );
};

export default Profile;
